import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { clientsAPI, returnsAPI, salesAPI, branchesAPI, getProducts, getCompanyAccounts, API_BASE_URL } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import SalesTransactionsTable from './SalesTransactionTable';
import SearchableDropdown from '../components/SearchableDropdown';
// import { productList } from '../utils/productList'; // Remote hardcoded import
import '../css/CustomerSalesDetail.css';

function CustomerSalesDetails() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);

    // console.log('=== FRONTEND DEBUG ===');
    // console.log('clientId:', clientId);
    // console.log('Full URL:', window.location.href);

    // ---------------- STATES ----------------
    const [customer, setCustomer] = useState(null);
    const [sales, setSales] = useState([]);
    const [showSaleModal, setShowSaleModal] = useState(false);
    const [saleForm, setSaleForm] = useState({
        transaction_type: 'sale', // Default to sale
        transaction_date: new Date().toISOString().split('T')[0], // Default to today
        // Legacy fields for backward compatibility or direct access
        product: '',
        quantity: '',
        unit_price: '',
        amount: '',
        discount: '',
        with_gst: true,
        // New: Multiple items
        items: [
            { product: '', quantity: '', unit_price: '', amount: '', discount: '', with_gst: true }
        ],
        total_amount: '',
        amount_paid: '',
        account_paid: '',
        reference_number: '',
        notes: '',
        executive_name: '',
        branch_name: '',
        payment_mode: '' // New field for Payment Received
    });
    const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [filterType, setFilterType] = useState('all'); // Filter by Transaction Type
    const [currentBalance, setCurrentBalance] = useState(0);
    const [accountSummary, setAccountSummary] = useState(null);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [openingBalanceDate, setOpeningBalanceDate] = useState('');
    const [openingBalanceLoading, setOpeningBalanceLoading] = useState(false);
    const [apiTransactionCount, setApiTransactionCount] = useState(null);
    // const [apiTransactionCount, setApiTransactionCount] = useState(null);
    const [salesExecutives, setSalesExecutives] = useState([]); // New state
    const [products, setProducts] = useState([]); // Dynamic product list
    const [branches, setBranches] = useState([]); // New state for branches
    const [companyAccounts, setCompanyAccounts] = useState([]); // New state for company accounts
    const [accountPaid, setAccountPaid] = useState(0); // Track account paid for current entry

    const isFirstEntry = transactions.length <= 1;

    // ---------------- FETCH CUSTOMER DATA ----------------
    useEffect(() => {
        // console.log('useEffect triggered with clientId:', clientId);

        if (clientId) {
            fetchCustomerData();
            fetchSalesExecutives(); // Fetch executives
            fetchProducts(); // Fetch products
            fetchBranches(); // Fetch branches
            fetchCompanyAccounts(); // Fetch company accounts
        } else {
            setError('No customer ID provided');
            setLoading(false);
        }
    }, [clientId]);

    const fetchProducts = async () => {
        try {
            const data = await getProducts();
            // Assuming data is array of objects {id, name, ...}
            // SearchableDropdown expects array of strings or objects? 
            // The previous productList was array of strings.
            // Let's map to strings if that's what SearchableDropdown expects, 
            // OR check SearchableDropdown implementation. 
            // Usually it handles strings well. 
            // Let's map to names:
            if (Array.isArray(data)) {
                setProducts(data.map(p => p.name));
            }
        } catch (error) {
            // console.error('Error fetching products:', error);
        }
    };

    const fetchSalesExecutives = async () => {
        try {
            const response = await salesAPI.getExecutiveOfficers();
            // API returns straight array: [{id, name, role}, ...]
            // Or if it was wrapped: { success: true, officers: [...] }
            const data = response.data;

            if (Array.isArray(data)) {
                const uniqueOfficers = [...new Set(data.filter(o => o.name).map(o => o.name))];
                setSalesExecutives(uniqueOfficers);
            } else if (data && data.success && Array.isArray(data.officers)) {
                const uniqueOfficers = [...new Set(data.officers.filter(o => o.name).map(o => o.name))];
                setSalesExecutives(uniqueOfficers);
            }
        } catch (error) {
            // console.error('Error fetching sales executives:', error);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await branchesAPI.getAll();
            if (response.data && response.data.success) {
                setBranches(response.data.branches || []);
            }
        } catch (error) {
            // console.error('Error fetching branches:', error);
        }
    };

    const fetchCompanyAccounts = async () => {
        try {
            const accounts = await getCompanyAccounts();
            setCompanyAccounts(accounts || []);
        } catch (error) {
            // console.error('Error fetching company accounts:', error);
        }
    };



    const fetchCustomerData = async () => {
        // console.log('fetchCustomerData called');

        try {
            setLoading(true);
            setError(null);

            // Fetch customer details
            const customerData = await fetchCustomerDetails(clientId);

            if (customerData) {
                // Pre-fill executive name if available
                if (customerData.executive_officer_name || customerData.sales_executive) {
                    setSaleForm(prev => ({
                        ...prev,
                        executive_name: customerData.executive_officer_name || customerData.sales_executive
                    }));
                }

                // Fetch opening balance
                await fetchOpeningBalance(clientId);

                // Fetch sales transactions
                await fetchCustomerSales(clientId);

                // Fetch transaction count
                await fetchTransactionCount(clientId);

                // console.log('All data loaded successfully');
            }
        } catch (err) {
            // console.error('Error in fetchCustomerData:', err);
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    };

    // ... (rest of methods until handleSaveSale) ...
    // Note: I will need to be careful with the replacement range to not delete methods I don't see.
    // Since I can't effectively "skip" the middle easily with replace_file_content unless I use multi_replace.
    // But I viewed the file in chunks.
    // I will use multi_replace to be safe and targeted.

    // Skipping replace_file_content for now in favor of multi_replace to avoid overwriting unseen code.


    const fetchCustomerDetails = async (id) => {
        // console.log('Fetching customer details for id:', id);

        try {
            const response = await clientsAPI.get(id);
            // console.log('API Response received:', response.data);

            if (response.data) {
                const normalizedCustomer = {
                    id: response.data.id,
                    client_name: response.data.name || response.data.client_name,
                    name: response.data.name,
                    phone: response.data.phone,
                    address: response.data.address,
                    gst_number: response.data.gst_number || response.data.gst_no,
                    email: response.data.email,
                    contact_person: response.data.contact_person,
                    sales_executive: response.data.sales_executive,
                    executive_officer_name: response.data.sales_executive,
                    account_status: response.data.account_status,
                    status: response.data.account_status,
                    client_type: response.data.client_type,
                    current_balance: response.data.current_balance,
                    opening_balance: response.data.opening_balance,
                    created_at: response.data.created_at,
                    updated_at: response.data.updated_at
                };

                // console.log('Normalized customer data:', normalizedCustomer);
                setCustomer(normalizedCustomer);
                return normalizedCustomer;
            }

            throw new Error('No data in response');
        } catch (err) {
            // console.error('Error fetching customer details:', err);
            throw err;
        }
    };

    // Add this function to fetch opening balance
    const fetchOpeningBalance = async (customerId) => {
        try {
            setOpeningBalanceLoading(true);
            // console.log('🔍 [1] Starting fetchOpeningBalance for ID:', customerId);
            // console.log('🔍 [2] Using clientsAPI.getOpeningBalance method');
            // console.log('🔍 [3] API URL would be:', `/api/sales-accounts/transactions/${customerId}/opening-balance`);

            const response = await clientsAPI.getOpeningBalance(customerId);
            // console.log('✅ [4] API Response received:', response);

            // Check if response.data exists
            if (!response.data) {
                // console.warn('⚠️ No data in response');
                setOpeningBalance(0);
                return;
            }

            // Our simplified API returns {success: true, opening_balance: X}
            if (response.data.success) {
                const openingBalance = response.data.opening_balance || 0;
                // console.log('✅ [8] Setting opening balance to:', openingBalance);
                setOpeningBalance(openingBalance);
            } else {
                // console.warn('⚠️ API returned success: false', response.data);
                setOpeningBalance(0);
            }
        } catch (error) {
            // console.error('❌ ERROR in fetchOpeningBalance:', error);
            // if (error.response) {
            //     console.error('❌ Error Response Data:', error.response.data);
            //     console.error('❌ Error Response Status:', error.response.status);
            //     console.error('❌ Error Response Headers:', error.response.headers);
            // } else if (error.request) {
            //     console.error('❌ No response received. Error request:', error.request);
            //     console.error('❌ This usually means:');
            //     console.error('❌ 1. Backend is not running');
            //     console.error('❌ 2. CORS issue');
            //     console.error('❌ 3. Network error');
            // } else {
            //     console.error('❌ Request setup error:', error.message);
            // }

            setOpeningBalance(0);
        } finally {
            // console.log('🔍 [9] Setting loading to false');
            setOpeningBalanceLoading(false);
        }
    };

    const fetchTransactionCount = async (id) => {
        try {
            const res = await clientsAPI.getTransactionCount(id);
            if (res.data.success) {
                // console.log('Transaction count fetched:', res.data.count);
                setApiTransactionCount(res.data.count);
            }
        } catch (err) {
            // console.error('Error fetching transaction count:', err);
        }
    };

    const fetchCustomerSales = async (id, startDate = null, endDate = null) => {
        // console.log('Fetching sales and returns for customer id:', id);

        try {
            // Parallel fetch: Sales + Returns
            const [salesResponse, returnsResponse] = await Promise.all([
                clientsAPI.getTransactions(id, { startDate, endDate }),
                returnsAPI.getAll({
                    client_id: id,
                    client_type: 'Customer',
                    startDate,
                    endDate
                })
            ]);

            // console.log('Sales API response:', salesResponse.data);
            // console.log('Returns API response:', returnsResponse.data);

            // 1. Process Sales
            const salesData = salesResponse.data.transactions || salesResponse.data || [];
            let salesTransactions = [];

            if (Array.isArray(salesData)) {
                const ledgerData = salesData;
                setSales(ledgerData);
                salesTransactions = transformSalesToTransactions(ledgerData, salesResponse.data.balanceSummary || {});
            }

            // 2. Process Returns
            const returnsData = returnsResponse.data || [];
            // Normalize Returns
            const returnTransactions = returnsData.map((ret, index) => {
                const refundAmount = parseFloat(ret.refund_amount || ret.total_amount || 0);
                const amountPaid = parseFloat(ret.amount_paid || 0);
                const netBalance = refundAmount - amountPaid;

                return {
                    id: `RET-${ret.id || index}`,
                    transaction_date: ret.date || ret.date_initiated || new Date().toISOString().split('T')[0],
                    date: ret.date || ret.date_initiated || new Date().toISOString().split('T')[0],
                    description: `Return: ${ret.product || ret.reason || 'Sales Return'}`,
                    product: ret.product || ret.reason || 'Return',
                    type: 'return', // This will map to 'return' badge in table logic
                    quantity: ret.quantity || 0,
                    unit_price: ret.unit_price || 0,
                    amount: refundAmount, // Display positive amount for row info
                    discount: ret.discount || 0,
                    with_gst: ret.with_gst ? 'Yes' : 'No',
                    total_amount: refundAmount,
                    amount_paid: amountPaid,
                    // Balance is NEGATIVE because it reduces what the customer owes (or what we owe them)
                    // If we owe them (Refund > Paid), it's a credit to them, so negative debt.
                    balance: -netBalance,
                    isOpeningBalance: false,
                    isSale: false,
                    transaction_type: 'return',
                    reference_number: ret.return_number || ret.return_id || '',
                    notes: ret.notes || ''
                };
            });

            // 3. Combine
            // We need to handle Opening Balance which might be in salesTransactions[0] if transformSalesToTransactions added it
            // transformSalesToTransactions adds opening balance. 
            // We should filter it out from salesTransactions if we want to sort globally, then re-add it? 
            // Or just merge arrays and sort (keeping Opening Balance pinned if possible, but transformSalesToTransactions pins it via date or ID?)

            // Actually `transformSalesToTransactions` *always* adds opening balance as the first item if it exists.
            // If we merge, we might lose that order if we just sort.

            // Let's extract Opening Balance if it exists
            const openingBalIndex = salesTransactions.findIndex(t => t.id === 'opening-balance');
            let openingBalTxn = null;
            if (openingBalIndex !== -1) {
                openingBalTxn = salesTransactions[openingBalIndex];
                salesTransactions.splice(openingBalIndex, 1);
            }

            const allTransactions = [...salesTransactions, ...returnTransactions].sort((a, b) =>
                new Date(b.transaction_date) - new Date(a.transaction_date)
            );

            // Re-insert Opening Balance at the end (or beginning? The sort above is DESCENDING date (newest first)).
            // Opening balance usually goes at the VERY BOTTOM (oldest).
            // So if we sort DESC, it naturally falls to bottom if date is old. 
            // But `transformSalesToTransactions` hardcodes opening balance date?
            // "date: customer?.created_at ... or salesData[0]..."
            // If we want it at the bottom (start of history), it should be last in a DESC list? 
            // No, DESC list means Newest Top. Oldest Bottom. Opening balance is oldest.
            // So just pushing it to the array before/after sort?
            // If we sort by date, it will likely be last.

            if (openingBalTxn) {
                // Ensure it's treated as oldest if dates clash?
                // Just let the date sort handle it, but if we want to force it last:
                allTransactions.push(openingBalTxn);
                // Wait, if we push it last, it's definitely last.
                // But we should verify if the sort puts it there anyway. 
                // transformSalesToTransactions adds it at the start, but then doesn't sort?
                // Actually transformSalesToTransactions sorts sales themselves (lines 362).

                // Let's just abide by date sorting which is robust.
                // But wait, `allTransactions` above is sorted DESC. So oldest is last.
                // So if we just include it in the sort, it works.
            }

            // But wait, I extracted it. Let me put it back into the list to be sorted.
            const listToSort = openingBalTxn ? [...salesTransactions, ...returnTransactions, openingBalTxn] : [...salesTransactions, ...returnTransactions];

            // Re-sort everything by date DESC
            listToSort.sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));

            setTransactions(listToSort);
            setFilteredTransactions(listToSort);

            // Update Counts
            const totalCount = (salesResponse.data.totalTransactions || salesData.length) + returnsData.length;
            setApiTransactionCount(totalCount);

        } catch (err) {
            // console.error('Error fetching sales and returns:', err);
            // Handle error...
            handleApiError(err);
        }
    };


    const handleApiError = (err) => {
        let errorMessage = 'Failed to load customer data';

        if (err.message === 'Network Error' || err.message.includes('Failed to fetch')) {
            errorMessage = 'Cannot connect to backend server. Please check:';
            errorMessage += `\n1. Is backend running on ${API_BASE_URL.replace('/api', '')}?`;
            errorMessage += '\n2. Check backend console for errors';
            errorMessage += `\n3. Try opening ${API_BASE_URL}/clients in browser`;
        } else if (err.response) {
            // console.error('API Error Response:', err.response);

            if (err.response.status === 400) {
                errorMessage = `Bad request: ${err.response.data?.message || 'Invalid data'}`;
            } else if (err.response.status === 404) {
                errorMessage = `Customer not found: ${clientId}`;
            } else if (err.response.status === 500) {
                errorMessage = 'Server error. Check backend logs.';
            } else {
                errorMessage = `Server returned ${err.response.status}: ${err.response.statusText}`;
            }
        } else if (err.request) {
            errorMessage = 'No response from server. Backend might be down.';
            errorMessage += '\n\nTest these in your browser:';
            errorMessage += `\n• ${API_BASE_URL.replace('/api', '')}/`;
            errorMessage += `\n• ${API_BASE_URL}/clients/${clientId}`;
        }

        setError(errorMessage);
    };

    // ---------------- CALCULATE SUMMARY ----------------
    const calculateAccountSummary = () => {
        try {
            // User Request: "the account summary current balance card show the total of the balance column value 
            // in the table to be subtracted from the value on the Total amount paid card"

            if (transactions && transactions.length > 0) {
                // Sum all gross balances (Invoice Totals / Opening Balances)
                const totalGrossBalance = transactions
                    .reduce((sum, txn) => sum + (parseFloat(txn.balance) || 0), 0);

                // Sum all payments made
                const totalAmountPaid = transactions
                    .reduce((sum, txn) => sum + (parseFloat(txn.amount_paid) || 0), 0);

                // Current Balance = Total Gross - Total Paid
                const currentBalance = totalGrossBalance - totalAmountPaid;

                // Update the state
                setCurrentBalance(currentBalance);

                // Calculate other stats
                const realTransactions = transactions.filter(t => !t.isOpeningBalance);
                const totalTransactions = apiTransactionCount !== null ? apiTransactionCount : realTransactions.length;

                setAccountSummary({
                    statistics: {
                        total_transactions: totalTransactions,
                        total_outstanding: currentBalance,
                        total_amount_paid: totalAmountPaid
                    }
                });
            } else {
                // If no transactions yet, fallback to opening balance
                const b = parseFloat(openingBalance) || 0;
                setCurrentBalance(b);
                setAccountSummary({
                    statistics: {
                        total_transactions: 0,
                        total_outstanding: b,
                        total_amount_paid: 0
                    }
                });
            }
        } catch (error) {
            // console.error('Error calculating summary:', error);
        }
    };

    useEffect(() => {
        calculateAccountSummary();
    }, [transactions, openingBalance, apiTransactionCount]);

    // ---------------- HELPER FUNCTIONS ----------------
    const transformSalesToTransactions = (salesData, balanceSummary = {}) => {
        const openingBalancePaid = balanceSummary.openingBalancePaid || 0;
        const openingAccountPaid = balanceSummary.openingAccountPaid || '';
        const openingGross = parseFloat(openingBalance) || 0;

        const transactions = [];

        // 1. Add opening balance row
        transactions.push({
            id: 'opening-balance',
            transaction_date: customer?.created_at
                ? new Date(customer.created_at).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
            date: customer?.created_at
                ? new Date(customer.created_at).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
            description: 'Opening Balance',
            product: 'Opening Balance',
            type: 'opening',
            quantity: 0,
            unit_price: 0,
            amount: 0,
            discount: 0,
            with_gst: 'No',
            total_amount: openingGross,
            amount_paid: openingBalancePaid,
            account_paid: openingAccountPaid,
            balance: openingGross, // Included for sum math, but visually hidden if needed
            isOpeningBalance: true,
            isSale: false,
            transaction_type: 'opening'
        });

        if (!salesData || salesData.length === 0) return transactions;

        const sortedSales = salesData
            .filter(sale => (sale.transaction_type || '').toLowerCase() !== 'opening')
            .sort((a, b) =>
                new Date(a.transaction_date || a.created_at) - new Date(b.transaction_date || b.created_at)
            );

        sortedSales.forEach((sale, index) => {
            const saleAmount = parseFloat(sale.total_amount || sale.amount || 0);
            const amountPaid = parseFloat(sale.amount_paid || 0);
            const typeLower = (sale.transaction_type || '').toLowerCase();

            // Use backend balance or calculate gross fallback
            let rowBalance = parseFloat(sale.balance);
            if (isNaN(rowBalance)) {
                if (['sale', 'cash', 'cheque', 'bank_transfer', 'upi'].includes(typeLower)) {
                    rowBalance = saleAmount;
                } else if (typeLower === 'return' || typeLower === 'credit_note') {
                    rowBalance = -saleAmount;
                } else {
                    rowBalance = 0;
                }
            }

            transactions.push({
                ...sale,
                id: sale.id || `tx-${index}`,
                transaction_date: sale.transaction_date || sale.created_at,
                date: sale.transaction_date || sale.created_at,
                product: sale.product || sale.transaction_type || 'Transaction',
                type: sale.transaction_type || 'transaction',
                amount_paid: amountPaid,
                total_amount: saleAmount,
                balance: rowBalance,
                isOpeningBalance: false,
                isSale: ['sale', 'cash', 'cheque', 'bank_transfer', 'upi'].includes(typeLower),
                transaction_type: typeLower
            });
        });

        return transactions;
    };

    const formatCurrency = (amount) => {
        const numAmount = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numAmount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // ---------------- HELPER FOR ITEMS ----------------
    const handleItemChange = (index, field, value) => {
        const newItems = [...saleForm.items];
        newItems[index][field] = value;

        // Recalculate row amount
        if (['quantity', 'unit_price', 'discount', 'with_gst'].includes(field)) {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].unit_price) || 0;
            const discount = parseFloat(newItems[index].discount) || 0;

            let amount = qty * price;
            newItems[index].amount = parseFloat(amount.toFixed(2));

            // Note: GST and Total Amount per row?
            // The main form usually calculates global GST/Total. 
            // But if we want per-row 'with_gst':
            // Let's assume the 'amount' field in item is the subtotal (qty * price).
            // We can store a row-level total if needed, but the main calculation usually aggregates.
        }

        setSaleForm(prev => {
            const updatedForm = { ...prev, items: newItems };
            return recalcTotal(updatedForm);
        });
    };

    const addItem = () => {
        setSaleForm(prev => {
            const isEstimate = (prev.branch_name || '').toLowerCase() === 'estimates';
            return {
                ...prev,
                items: [
                    ...prev.items,
                    { product: '', quantity: '', unit_price: '', amount: '', discount: '', with_gst: !isEstimate }
                ]
            };
        });
    };

    const removeItem = (index) => {
        if (saleForm.items.length > 1) {
            const newItems = saleForm.items.filter((_, i) => i !== index);
            setSaleForm(prev => {
                const updatedForm = { ...prev, items: newItems };
                return recalcTotal(updatedForm);
            });
        }
    };

    // ---------------- RECALCULATE TOTAL AMOUNT ----------------
    // ---------------- RECALCULATE TOTAL AMOUNT ----------------
    const recalcTotal = (nextForm) => {
        const form = { ...nextForm };
        const SALE_TYPES = ['cash', 'cheque', 'bank_transfer', 'upi', 'sale'];

        if (SALE_TYPES.includes((form.transaction_type || '').toLowerCase())) {
            let grandTotal = 0;
            let totalSubtotal = 0;
            let totalGST = 0;

            // Calculate subtotal from items
            if (form.items && form.items.length > 0) {
                form.items.forEach(item => {
                    const qty = Number(item.quantity || 0);
                    const price = Number(item.unit_price || 0);
                    const itemAmount = qty * price;

                    totalSubtotal += itemAmount;

                    // Calculate GST per item if checked
                    if (item.with_gst) {
                        totalGST += itemAmount * 0.18;
                    }
                });

                form.amount = Number(totalSubtotal.toFixed(2));
            }

            // Apply Discount (Global discount subtracted from the total)
            const discount = Number(form.discount || 0);

            // Grand Total = Subtotal + Total GST - Discount
            // Ensure we don't go below zero
            grandTotal = (totalSubtotal + totalGST) - discount;
            if (grandTotal < 0) grandTotal = 0;

            // Apply Round-off Logic
            // If the total amount is less than .50, it should remain as is.
            // If the total amount is .50 or above, it should be rounded up to the nearest whole number.
            // Actually, standard Math.round does this: 0.5 -> 1, 0.49 -> 0.
            form.total_amount = Math.round(grandTotal);

        } else {
            // payment / credit_note / opening
            const amount = Number(form.amount || 0);
            form.total_amount = Number(amount.toFixed(2));
        }

        return form;
    };

    // ---------------- HANDLE NEW TRANSACTION ----------------
    const handleSaveSale = async () => {
        try {
            if (!saleForm.transaction_type) {
                alert('Please select a transaction type');
                return;
            }

            if (!saleForm.transaction_date) {
                alert('Please select a transaction date');
                return;
            }

            const transactionPayload = {
                transaction_type: saleForm.transaction_type === 'payment' ? 'payment' : saleForm.transaction_type,
                // Aggregated or first item details for backward compatibility / list view summary
                product: saleForm.transaction_type === 'payment'
                    ? `Payment - ${saleForm.payment_mode || 'Cash'}`
                    : (saleForm.items.length > 0 ? saleForm.items[0].product : ''),
                quantity: saleForm.transaction_type === 'payment' ? 1
                    : saleForm.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
                unit_price: 0, // Mixed
                amount: Number(saleForm.amount || 0),
                discount: Number(saleForm.discount || 0), // Total Discount
                with_gst: saleForm.with_gst, // Global flag (might be less relevant if per-item)
                total_amount: Number(saleForm.total_amount || 0),
                amount_paid: saleForm.transaction_type === 'payment' ? Number(saleForm.amount || 0) : Number(saleForm.amount_paid || 0),
                account_paid: saleForm.account_paid || '',
                transaction_date: saleForm.transaction_date,
                reference_number: saleForm.reference_number || '',
                notes: saleForm.notes || '',
                client_id: clientId,
                client_id: clientId,
                executive_name: saleForm.executive_name || '', // Send executive name
                branch_name: saleForm.branch_name || '', // Send branch name
                items: saleForm.transaction_type === 'payment' ? [] : saleForm.items // Send full items array
            };

            // Log what we're sending
            // console.log('Sending transaction payload:', transactionPayload);

            // Send to API
            await clientsAPI.addTransaction(transactionPayload);

            // Reset form and close modal
            setShowSaleModal(false);
            setSaleForm({
                transaction_type: 'sale',
                transaction_date: new Date().toISOString().split('T')[0],
                product: '',
                quantity: '',
                unit_price: '',
                amount: '',
                discount: '',
                with_gst: true,
                total_amount: '',
                amount_paid: '',
                account_paid: '',
                reference_number: '',
                notes: '',
                payment_mode: '',
                executive_name: customer?.executive_officer_name || customer?.sales_executive || ''
            });

            // Refresh data
            fetchCustomerData();

            alert('Transaction saved successfully!');
        } catch (err) {
            console.error('Failed to save transaction:', err);
            alert(`Failed to save transaction: ${err.message || 'Check backend connection'}`);
        }
    };

    // ---------------- RESET FORM WHEN MODAL OPENS ----------------
    useEffect(() => {
        if (showSaleModal) {
            setSaleForm({
                transaction_type: 'sale',
                transaction_date: new Date().toISOString().split('T')[0],
                product: '',
                quantity: '',
                unit_price: '',
                amount: '',
                discount: '',
                with_gst: true,
                items: [
                    { product: '', quantity: '', unit_price: '', amount: '', discount: '', with_gst: true }
                ],
                total_amount: '',
                amount_paid: '',
                account_paid: '',
                reference_number: '',
                notes: '',
                payment_mode: '',
                executive_name: customer?.executive_officer_name || customer?.sales_executive || ''
            });
        }
    }, [showSaleModal, customer]);

    // ---------------- FILTER TRANSACTIONS ----------------
    useEffect(() => {
        const filtered = transactions.filter(transaction => {
            // Apply Date Filters
            if (fromDate || toDate) {
                const transactionDate = new Date(transaction.date);
                if (fromDate && transactionDate < new Date(fromDate)) return false;
                if (toDate && transactionDate > new Date(toDate)) return false;
            }

            // Apply Type Filter
            if (filterType !== 'all') {
                const type = (transaction.transaction_type || '').toUpperCase();
                return type === filterType.toUpperCase();
            }

            return true;
        });
        setFilteredTransactions(filtered);
    }, [fromDate, toDate, transactions, filterType]);

    // ---------------- RENDER FUNCTIONS ----------------
    if (loading) {
        return (
            <div className="account-details-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading customer details...</p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        Customer ID: <code>{clientId}</code>
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="account-details-container">
                <div className="error-message">
                    <h3>⚠️ Error Loading Customer</h3>
                    <div style={{ whiteSpace: 'pre-line', margin: '15px 0' }}>
                        {error.split('\n').map((line, index) => (
                            <p key={index} style={{ margin: '5px 0' }}>{line}</p>
                        ))}
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => fetchCustomerData()}
                    >
                        Retry Loading
                    </button>
                </div>
            </div>
        );
    }

    // ---------------- MAIN RENDER ----------------
    return (
        <div className="account-details-container">
            {/* HEADER */}
            <div className="header-section">
                <h1>Customer Account Details</h1>
                <div className="action-buttons">
                    {user?.role !== 'normal_user' && (
                        <button className="btn btn-primary" onClick={() => setShowSaleModal(true)}>
                            + New Transaction
                        </button>
                    )}
                    <button className="btn btn-reset" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                </div>
            </div>

            {/* CUSTOMER INFO */}
            {customer && (
                <div className="customer-info-card">
                    <div className="customer-header">
                        <h2>{customer.client_name || customer.name || 'Unnamed Customer'}</h2>

                        <div className="account-number">
                            Customer ID: <code>{customer.id}</code>
                            <span style={{
                                marginLeft: '10px',
                                padding: '2px 8px',
                                backgroundColor: customer.status === 'Active' ? '#d4edda' : '#f8d7da',
                                color: customer.status === 'Active' ? '#155724' : '#721c24',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                            }}>
                                {customer.status || customer.account_status || 'Unknown'}
                            </span>
                        </div>
                    </div>

                    {/* CONTACT DETAILS */}
                    <div className="customer-details-grid">
                        <div className="detail-item">
                            <label>Phone</label>
                            <span>{customer.phone || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Email</label>
                            <span>{customer.email || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Address</label>
                            <span>{customer.address || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Contact Person</label>
                            <span>{customer.contact_person || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Vendor Type</label>
                            <span>{customer.vendor_type || customer.client_type || '-'}</span>
                        </div>

                        <div className="detail-item">
                            <label>Executive Officer</label>
                            <span>{customer.executive_officer_name || customer.sales_executive || '-'}</span>
                        </div>
                    </div>

                    {/* ACCOUNT SUMMARY */}
                    <div className="account-summary-section" style={{
                        marginTop: '20px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        <h3 style={{ marginBottom: '15px', color: '#333' }}>Account Summary</h3>
                        <div className="summary-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px'
                        }}>
                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Opening Balance</label>
                                <span className={openingBalance >= 0 ? 'text-success' : 'text-danger'}
                                    style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(openingBalance)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Current Balance</label>
                                <span className={currentBalance >= 0 ? 'text-success' : 'text-danger'}
                                    style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(currentBalance)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Total Transactions</label>
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {apiTransactionCount !== null ? apiTransactionCount : (accountSummary?.statistics?.total_transactions || 0)}
                                </span>
                            </div>

                            <div className="summary-item" style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '5px'
                                }}>Total Amount paid</label>
                                <span className="text-primary" style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                    {formatCurrency(transactions.reduce((sum, txn) => sum + (parseFloat(txn.amount_paid) || 0), 0))}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )
            }

            {/* TRANSACTIONS TABLE */}

            <SalesTransactionsTable
                transactions={transactions}
                filteredTransactions={filteredTransactions}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                customerId={clientId}
                customerName={customer?.client_name || customer?.name || 'Customer'}
                fromDate={fromDate}
                toDate={toDate}
                setFromDate={setFromDate}
                setToDate={setToDate}
                onFetch={() => fetchCustomerSales(clientId, fromDate, toDate)}
                filterType={filterType}
                setFilterType={setFilterType}
            />

            {/* TRANSACTION MODAL */}
            {
                showSaleModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h3>Add New Transaction</h3>
                                <button
                                    className="modal-close"
                                    onClick={() => setShowSaleModal(false)}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="form-grid">


                                    {/* Executive Officer */}
                                    <div className="form-group">
                                        <label>Sales Executive</label>
                                        <select
                                            className="form-input"
                                            value={saleForm.executive_name}
                                            onChange={e =>
                                                setSaleForm(prev => ({
                                                    ...prev,
                                                    executive_name: e.target.value
                                                }))
                                            }
                                        >
                                            <option value="">Select Executive</option>
                                            {salesExecutives.map((exec, idx) => (
                                                <option key={idx} value={exec}>
                                                    {exec}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Branch Selection */}
                                    <div className="form-group">
                                        <label>Branch *</label>
                                        <select
                                            className="form-input"
                                            value={saleForm.branch_name}
                                            onChange={e => {
                                                const selectedBranch = e.target.value;
                                                const isEstimate = selectedBranch.toLowerCase() === 'estimates';

                                                setSaleForm(prev => {
                                                    // Automatically update all items' GST status based on branch
                                                    const updatedItems = prev.items.map(item => ({
                                                        ...item,
                                                        with_gst: !isEstimate
                                                    }));

                                                    return recalcTotal({
                                                        ...prev,
                                                        branch_name: selectedBranch,
                                                        items: updatedItems
                                                    });
                                                });
                                            }}
                                            required
                                        >
                                            <option value="">Select Branch</option>
                                            {branches.map((branch) => (
                                                <option key={branch.id} value={branch.name}>
                                                    {branch.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Transaction Type */}
                                    <div className="form-group">
                                        <label>Transaction Type *</label>
                                        <select
                                            className="form-input"
                                            value={saleForm.transaction_type}
                                            onChange={e =>
                                                setSaleForm(prev =>
                                                    recalcTotal({
                                                        ...prev,
                                                        transaction_type: e.target.value
                                                    })
                                                )
                                            }
                                            required
                                        >
                                            <option value="select_type">Select Type</option>
                                            <option value="SALE">Sales Invoice</option>
                                            <option value="RETURN">Return</option>
                                        </select>
                                    </div>

                                    {/* Payment Mode (Only for Payment Type) */}
                                    {saleForm.transaction_type === 'payment' && (
                                        <div className="form-group">
                                            <label>Payment Mode *</label>
                                            <select
                                                className="form-input"
                                                value={saleForm.payment_mode}
                                                onChange={e =>
                                                    setSaleForm(prev => ({
                                                        ...prev,
                                                        payment_mode: e.target.value
                                                    }))
                                                }
                                                required
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Cheque">Cheque</option>
                                                <option value="Bank Transfer">Bank Transfer</option>
                                                <option value="UPI">UPI</option>
                                            </select>
                                        </div>
                                    )}

                                    {/* Date */}
                                    <div className="form-group">
                                        <label>Date *</label>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={saleForm.transaction_date}
                                            onChange={e =>
                                                setSaleForm(prev => ({
                                                    ...prev,
                                                    transaction_date: e.target.value
                                                }))
                                            }
                                            required
                                        />
                                    </div>

                                    {/* ===================== COMMON SALE FIELDS ===================== */}
                                    {saleForm.transaction_type !== 'select_type' &&
                                        saleForm.transaction_type !== 'opening' && (
                                            <>
                                                {/* ITEMS LIST */}
                                                <div className="items-section">
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                        <label style={{ fontWeight: 'bold' }}>Items</label>
                                                    </div>

                                                    {/* Table Header */}
                                                    <div className="items-header" style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 60px 40px',
                                                        gap: '10px',
                                                        marginBottom: '5px',
                                                        padding: '0 10px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        color: '#555'
                                                    }}>
                                                        <div>Product</div>
                                                        <div>Qty</div>
                                                        <div>Price (₹)</div>
                                                        <div>Total (₹)</div>
                                                        <div style={{ textAlign: 'center' }}>GST</div>
                                                        <div></div>
                                                    </div>

                                                    {saleForm.items.map((item, index) => (
                                                        <div key={index} className="item-row" style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 60px 40px',
                                                            gap: '10px',
                                                            alignItems: 'center',
                                                            marginBottom: '8px',
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #eee',
                                                            padding: '8px 10px',
                                                            borderRadius: '6px'
                                                        }}>
                                                            <div style={{ marginBottom: 0 }}>
                                                                <SearchableDropdown
                                                                    options={products}
                                                                    value={item.product}
                                                                    onChange={(val) => handleItemChange(index, 'product', val)}
                                                                    placeholder="Select Product"
                                                                />
                                                            </div>
                                                            <div style={{ marginBottom: 0 }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    value={item.quantity}
                                                                    onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                                                                    placeholder="0"
                                                                    style={{ margin: 0 }}
                                                                />
                                                            </div>
                                                            <div style={{ marginBottom: 0 }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    value={item.unit_price}
                                                                    onChange={e => handleItemChange(index, 'unit_price', e.target.value)}
                                                                    placeholder="0.00"
                                                                    style={{ margin: 0 }}
                                                                />
                                                            </div>
                                                            <div style={{ marginBottom: 0 }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-input"
                                                                    value={(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2)}
                                                                    readOnly
                                                                    style={{ backgroundColor: '#f8f9fa', margin: 0, fontWeight: 'bold' }}
                                                                />
                                                            </div>
                                                            <div style={{ marginBottom: 0, textAlign: 'center' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={item.with_gst}
                                                                    onChange={e => handleItemChange(index, 'with_gst', e.target.checked)}
                                                                    style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                                                                    title="Apply 18% GST"
                                                                />
                                                            </div>

                                                            <button
                                                                type="button"
                                                                onClick={() => removeItem(index)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: '#dc3545',
                                                                    cursor: 'pointer',
                                                                    fontSize: '20px',
                                                                    padding: '0',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                                title="Remove Item"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}

                                                    <button
                                                        type="button"
                                                        onClick={addItem}
                                                        className="btn"
                                                        style={{
                                                            marginTop: '5px',
                                                            marginBottom: '15px',
                                                            backgroundColor: '#e2e6ea',
                                                            color: '#495057',
                                                            padding: '6px 12px',
                                                            fontSize: '13px',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        + Add Another Item
                                                    </button>
                                                </div>

                                                {/* Subtotal Selection Display */}
                                                <div className="form-row" style={{ marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                                                    <div className="form-group">
                                                        <label>Subtotal (All Items)</label>
                                                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(saleForm.amount)}</span>
                                                    </div>

                                                    <div className="form-group">
                                                        <label>Total Discount</label>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            value={saleForm.discount}
                                                            onChange={e => {
                                                                setSaleForm(prev => recalcTotal({ ...prev, discount: e.target.value }))
                                                            }}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Total */}
                                                <div className="form-group" style={{
                                                    backgroundColor: '#e8f5e9',
                                                    padding: '10px',
                                                    borderRadius: '6px',
                                                    marginTop: '10px'
                                                }}>
                                                    <label style={{ color: '#2e7d32', fontWeight: 'bold' }}>Grand Total (Inc. GST) *</label>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={formatCurrency(saleForm.total_amount)}
                                                        readOnly
                                                        required
                                                        style={{ fontWeight: 'bold', color: '#2e7d32', fontSize: '18px' }}
                                                    />
                                                </div>
                                            </>
                                        )}

                                    {/* ===================== OPENING / PAYMENT AMOUNT ===================== */}
                                    {(saleForm.transaction_type === 'opening' ||
                                        saleForm.transaction_type === 'payment' ||
                                        saleForm.transaction_type === 'credit_note') && (
                                            <div className="form-group">
                                                <label>
                                                    {saleForm.transaction_type === 'opening'
                                                        ? 'Opening Balance (₹)'
                                                        : 'Amount (₹)'}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    value={saleForm.amount}
                                                    onChange={e =>
                                                        setSaleForm(prev =>
                                                            recalcTotal({
                                                                ...prev,
                                                                amount: e.target.value
                                                            })
                                                        )
                                                    }
                                                    placeholder="0.00"
                                                />
                                            </div>
                                        )}

                                    {/* Removed Amount Paid and Account Paid fields as Payments are now handled from Payment Management */}

                                    {/* Invoice */}
                                    <div className="form-group">
                                        <label>Invoice Number</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={saleForm.reference_number}
                                            onChange={e =>
                                                setSaleForm(prev => ({
                                                    ...prev,
                                                    reference_number: e.target.value
                                                }))
                                            }
                                        />
                                    </div>

                                    {/* Notes */}
                                    <div className="form-group">
                                        <label>Notes</label>
                                        <textarea
                                            className="form-input"
                                            rows="3"
                                            value={saleForm.notes}
                                            onChange={e =>
                                                setSaleForm(prev => ({
                                                    ...prev,
                                                    notes: e.target.value
                                                }))
                                            }
                                        />
                                    </div>

                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn btn-cancel"
                                    onClick={() => setShowSaleModal(false)}
                                >
                                    Cancel
                                </button>
                                <button className="btn btn-save" onClick={handleSaveSale}>
                                    Save Transaction
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

        </div >
    );
}

export default CustomerSalesDetails;